import { useCallback, useEffect, useState } from "react";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import { ALL_INFO_STEPS, ATTEMPT_VALIDATE_DOB } from "../../const/AppConst";
import useAppState from "../../context/AppContext";
import { AppState } from "../../context/reducers/AppReducer";
import { getPropositionById, getPropositionCart, initSubscription, validateBirthDate } from "../../services/CustomerApiService";
import { displaySteps, getStartPage, isPropositionAfterSignature, isPropositionInDraft } from "../../utils/dataUtils";
import { isMgpAdmin } from "../../utils/permissionUtils";
import { extractJwtToken, hasValidateJwt, isValidDate } from '../../utils/utils';
import LoaderActionComponent from "../common/LoaderActionComponent";
import MgpFormInput from "../../elements/MgpFormInput/MgpFormInput";
import LoaderModal from "../../modals/common/LoaderModal";
import { AxiosError } from "axios";
import { IApiErrors } from "../../context/model";

type AuthForm = {
  accessCode: string;
};
export default function LoginComponent() {
  const {  state: { expiredToken },
    dispatch,
  } = useAppState();


  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [initLink, setInitLink] = useState<"success" | "error" | "init" | "validate">("init");
  const [searchParams, setSearchParams] = useSearchParams();
  const [birthDate, setBirthDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts ] = useState<number>(ATTEMPT_VALIDATE_DOB);
  const [token, setToken ] = useState<string>("");

  let initToken = searchParams.get("id");
  let jwtParams = searchParams.get("token");
  let isDemoMode = searchParams.get("demo");
  let isConseiller = false;
  let stepCode = searchParams.get("step");
  const navigate = useNavigate();

  const validateToken = () => {
    const extractedJwt = extractJwtToken(jwtParams, true);

    if (!initToken || !jwtParams || !extractedJwt || initToken !== extractedJwt.propositionId) {
      setErrorMsg("Ce lien n'est pas valide. Merci de reprendre votre lien d'affiliation dans votre boite mail.");
      setInitLink("error");
    } else if (extractedJwt?.validateDob) {
      setInitLink("validate");
    } else {
      setInitLink("success");
      setToken(jwtParams);
    }
  }

  useEffect(()=> {
    if (jwtParams) {
      validateToken();
    } else if (initToken) {
      setInitLink("validate");
    } else {
      setInitLink("error");
      setErrorMsg("Ce lien n'est pas valide. Merci de reprendre votre lien d'affiliation dans votre boite mail.");
    }
  }, []);

  const handleValidateBirthDate = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    if (!initToken) {
      setInitLink("error");
      setErrorMsg("Ce lien n'est pas valide. Merci de reprendre votre lien d'affiliation dans votre boite mail.");
      return;
    }
    try {
      const response = await validateBirthDate(initToken, birthDate);
      setIsLoading(false);
      if (response.data.valid) {
        setInitLink("success");
        setToken(response.data.token);
      } else {
        
        if (attempts > 1) {
          setErrorMsg("Nous n'avons pas pu valider votre date de naissance. Veuillez vérifier les informations fournies.");
          setInitLink("validate");
          setAttempts(attempts - 1 )
        } else {
          setInitLink("error");
          setErrorMsg("Le nombre de tentatives autorisées a été atteint, veuillez contacter votre employeur.");
          if (initToken && jwtParams) {
            searchParams.delete("token");
            searchParams.delete("id");
            setSearchParams(searchParams);
          }
        }
      }
    } catch (error) {
        setIsLoading(false);
        setInitLink("error");
        const err = error as AxiosError;
        console.log(err);
        if(err.response && err.response.status === 400) {
          const errorData = err.response?.data as IApiErrors;
          setErrorMsg("Erreur: " + errorData.message);
        } else if(err.response && err.response.status === 401) {
          const errorData = err.response?.data as IApiErrors;
          setErrorMsg("Accès refusé : veuillez vous connecter via le portail avant de commencer le parcours d'affiliation.");
        } else {
          setErrorMsg("Une erreur technique est survenue.");
        }
    }
  }

  const accessWithJwt = useCallback(async () => {
    setErrorMsg(null);

    const extractedJwt = extractJwtToken(token, true);

    if (!initToken || !extractedJwt || initToken !== extractedJwt.propositionId) {
      setErrorMsg("Ce lien n'est pas valide. Merci de reprendre votre lien d'affiliation dans votre boite mail.");
      setInitLink("error");
      return;
    }

    if (!token) {
      setErrorMsg("Une erreur technique est survenue. Veuillez réessayer ou contacter le support MGP");
      setInitLink("error");
      return;
    }

    if (!hasValidateJwt(extractedJwt)) {
      try {
        // to see more
        if(!expiredToken || expiredToken !== token) {
          await initSubscription(token, initToken);
          dispatch({type: 'INIT_LINK', jwt: token});
        }
        
        setErrorMsg("Ce lien est expiré. Nous venons de vous envoyer un nouveau lien par email.");
        setInitLink("error");
      } catch (error) {
        console.log(error);
        setErrorMsg("Une erreur technique est survenue");
        setInitLink("error");
      }
      return;
    }

    isConseiller = isMgpAdmin(extractedJwt)

    dispatch({type: 'LOGIN', id: initToken, jwt: token, isDemo: isDemoMode === 'true', isConseiller: isConseiller});

    try {
      const propositionPayload = await getPropositionById(token, initToken);
      let proposition = propositionPayload.data;
      let cartVal = undefined;
      let currentStepPath = '' as string;

      if(isPropositionAfterSignature(propositionPayload.data) && !isConseiller) {
        setErrorMsg("Votre parcours est finalisé, vous ne pouvez plus y accédez. Pour toute question complémentaire, veuillez contacter un conseiller.");
        setIsLoading(false);
        setInitLink("error");
        return;
      };

      if(isPropositionInDraft(propositionPayload.data) && propositionPayload.data.currentStep) {
          try {
            const cartPayload = await getPropositionCart(token, initToken);
            cartVal = cartPayload.data;
            if(cartVal?.data) {
              let cartState:AppState = JSON.parse(cartVal?.data);
              if(cartState.proposition) {
                proposition = cartState.proposition;
                if(cartState.lastStepPath) {
                  const stepFound = ALL_INFO_STEPS.find(step => step.path === cartState.lastStepPath);
                  currentStepPath = stepFound?.path??'';
                }
              }
            } else if (propositionPayload.data.currentStep === "Confirmé" || propositionPayload.data.currentStep === "CONFIRME") {
                const stepFound = ALL_INFO_STEPS.find(step => step.code === "CONFIRME");
                currentStepPath = stepFound?.path??'';
            }
          } catch(e) {
            console.log("Error during getting cart");
          }        
      }

      dispatch({type: 'LOAD_PROPOSITION', proposition: proposition, cartData: cartVal?.data});

      let steps = displaySteps(proposition);

      if (currentStepPath) {
        const index = steps.findIndex(step => step.path === currentStepPath);
        steps.forEach((step, i) => {
          if (i<=index) {
            step.state = "ACTIVE"
          }
        })
      }

      dispatch({type: "UPDATE_STEPS", steps: steps});

      const startPage = getStartPage(propositionPayload.data, currentStepPath, stepCode, isConseiller);
      navigate({
        pathname: startPage,
        search: createSearchParams({
            id: propositionPayload.data.propositionId
        }).toString(),
    }, {replace: true});
   
    } catch (error) {
      console.log(error);
      setErrorMsg("Une erreur technique est survenue");
      setInitLink("error");
    }
  }, [token]);
  return (
    <>
      {
        initLink === "validate" && 
        <form>
          <div className='form-row'>
            <div className="form-group mw-100">  
              <MgpFormInput
                  label="Veuillez saisir votre date de naissance"
                  id=""
                  required={true}
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  type="date"
              />
            </div>
          </div>
          <button id="submit-dob-button" className="cta-bleu small mt-md" onClick={handleValidateBirthDate} disabled={!isValidDate(birthDate)}>Continuer</button>
          {isLoading&&<LoaderModal hasMessage={false}/>}
        </form>
      }
      {token && initLink === "success" && <LoaderActionComponent onLoad={accessWithJwt}/>}

      {errorMsg && <div className='form-row mt-md'>
            <div className="form-group mw-100">  
              <p className="bg-fushia blanc p-sm">{errorMsg}</p>
            </div>
          </div>}
    </>
  );
}
